import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  LoaderCircle,
  Music2,
  Plus,
  ShieldCheck,
  Trash2,
  UsersRound,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import type { SocialAccounts, SocialConnection, SocialProvider } from "./types";

type Props = {
  accounts: SocialAccounts | null;
  onAccountsChange: (accounts: SocialAccounts) => void;
  onClose: () => void;
  announce: (message: string) => void;
};

function providerLabel(provider: SocialProvider): string {
  return provider === "instagram" ? "Instagram" : "TikTok";
}

function accountLabel(account: SocialConnection): string {
  return account.username ? `@${account.username}` : account.display_name || "연결된 계정";
}

function accountCapability(account: SocialConnection): string {
  if (account.provider === "instagram") return "Reels 직접 게시";
  const capabilities = [];
  if (account.scopes.includes("video.publish")) capabilities.push("직접 게시");
  if (account.scopes.includes("video.upload")) capabilities.push("앱 초안 전송");
  return capabilities.join(" · ") || "게시 권한 확인 필요";
}

export function SocialAccountsManager({ accounts, onAccountsChange, onClose, announce }: Props) {
  const [loading, setLoading] = useState(!accounts);
  const [connecting, setConnecting] = useState<SocialProvider | null>(null);
  const [disconnecting, setDisconnecting] = useState("");
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.socialAccounts();
      onAccountsChange(response);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "게시 계정을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [onAccountsChange]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !connecting && !disconnecting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [connecting, disconnecting, onClose]);

  async function connect(provider: SocialProvider) {
    setConnecting(provider);
    setError("");
    try {
      const response = await api.connectSocial(provider);
      window.location.assign(response.url);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : `${providerLabel(provider)} 연결을 시작하지 못했습니다.`);
      setConnecting(null);
    }
  }

  async function disconnect(account: SocialConnection) {
    if (!window.confirm(`${providerLabel(account.provider)} ${accountLabel(account)} 계정 연결을 해제할까요?\n이미 게시된 영상에는 영향이 없습니다.`)) return;
    setDisconnecting(account.id);
    setError("");
    try {
      await api.disconnectSocial(account.id);
      await reload();
      announce(`${providerLabel(account.provider)} ${accountLabel(account)} 연결을 해제했습니다.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "계정 연결을 해제하지 못했습니다.");
    } finally {
      setDisconnecting("");
    }
  }

  const connectedCount = accounts?.connections.length || 0;

  return (
    <div className="dialog-backdrop social-accounts-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !connecting && !disconnecting) onClose();
    }}>
      <section className="social-accounts-dialog" role="dialog" aria-modal="true" aria-labelledby="social-accounts-title">
        <header className="social-accounts-heading">
          <div>
            <span className="social-accounts-mark"><UsersRound size={19} /></span>
            <span>
              <h2 id="social-accounts-title">게시 계정 관리</h2>
              <p>계정을 미리 연결하고, 완성 영상마다 올릴 계정만 선택하세요.</p>
            </span>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="게시 계정 관리 닫기"><X size={19} /></button>
        </header>

        <div className="social-accounts-summary">
          {connectedCount ? <CheckCircle2 size={18} /> : <ShieldCheck size={18} />}
          <span>
            <strong>{connectedCount ? `${connectedCount}개 계정 연결됨` : "아직 연결된 게시 계정이 없습니다"}</strong>
            <small>로그인 정보는 저장하지 않고 플랫폼이 발급한 토큰만 암호화해 보관합니다.</small>
          </span>
        </div>

        {error && <div className="social-error" role="alert"><AlertTriangle size={16} /><span>{error}</span></div>}

        {loading && !accounts ? (
          <p className="social-accounts-loading"><LoaderCircle className="spin" size={17} />계정 연결 상태 확인 중</p>
        ) : (
          <div className="social-provider-list">
            {(["instagram", "tiktok"] as const).map((provider) => {
              const config = accounts?.providers[provider];
              const connections = accounts?.connections.filter((account) => account.provider === provider) || [];
              return (
                <section className="social-provider-section" key={provider} aria-labelledby={`social-provider-${provider}`}>
                  <header>
                    <span className="social-platform-icon">{provider === "instagram" ? <Camera size={17} /> : <Music2 size={17} />}</span>
                    <span>
                      <strong id={`social-provider-${provider}`}>{providerLabel(provider)}</strong>
                      <small>{connections.length ? `${connections.length}개 연결됨` : "게시할 계정을 추가하세요"}</small>
                    </span>
                    <em className={config?.configured ? "ready" : "setup"}>{config?.configured ? "연결 가능" : "앱 설정 필요"}</em>
                  </header>

                  <div className="social-connected-list">
                    {connections.map((account) => (
                      <div className="social-connected-account" key={account.id}>
                        {account.avatar_url
                          ? <img src={account.avatar_url} alt="" />
                          : <span className="social-account-fallback">{provider === "instagram" ? "IG" : "TT"}</span>}
                        <span>
                          <strong>{accountLabel(account)}</strong>
                          <small>{account.display_name && account.username ? `${account.display_name} · ` : ""}{accountCapability(account)}</small>
                        </span>
                        <button className="icon-button danger" onClick={() => void disconnect(account)} disabled={disconnecting === account.id} aria-label={`${accountLabel(account)} 연결 해제`}>
                          {disconnecting === account.id ? <LoaderCircle className="spin" size={16} /> : <Trash2 size={16} />}
                        </button>
                      </div>
                    ))}
                    {!connections.length && (
                      <div className="social-provider-empty">
                        <span>{provider === "instagram" ? "Reels 게시 계정" : "TikTok 게시 계정"}</span>
                        <small>여러 계정을 차례로 추가할 수 있습니다.</small>
                      </div>
                    )}
                  </div>

                  <footer>
                    <span>{config?.configured ? "다른 계정으로 로그인하면 목록에 하나 더 추가됩니다." : config?.reason}</span>
                    <button className="button subtle compact" onClick={() => void connect(provider)} disabled={!config?.configured || Boolean(connecting)}>
                      {connecting === provider ? <LoaderCircle className="spin" size={15} /> : <Plus size={15} />}
                      {connections.length ? "다른 계정 추가" : "계정 추가"}
                    </button>
                  </footer>
                </section>
              );
            })}
          </div>
        )}

        <footer className="social-accounts-actions">
          <p><ShieldCheck size={15} />게시 직전에는 선택한 영상·계정·문구·공개 범위를 다시 확인합니다.</p>
          <button className="button primary compact" onClick={onClose}>완료</button>
        </footer>
      </section>
    </div>
  );
}
